import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Shield, ArrowLeft, Inbox, Tag, CheckCircle2, XCircle, Clock,
  Loader2, RefreshCw, ExternalLink, DollarSign, Save, Search,
  MessageSquare, MessageCircle, Send, User, ShieldCheck, Mail,
  Video, Sparkles, Ticket, Users, Handshake, Trophy, Plus, Minus, Dices, Trash2, Radio, Settings, Eye, EyeOff, Newspaper, ShoppingBag,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { SPRITES, RARITIES, VARIANTS } from '../mock';
import { buildPriceMap, defaultPrice } from '../lib/pricing';
import { toast } from '../hooks/use-toast';
import LotteryWheelDialog from '../components/LotteryWheelDialog';
import OfferChatDialog from '../components/OfferChatDialog';
import SiteStatsBar from '../components/SiteStatsBar';
import { SeasonManagerCard } from '../components/SeasonManagerCard';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const STATUS_TABS = ['pending', 'accepted', 'declined'];

export default function AdminPanel() {
  const { user, isAuthed, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('offers');
  const [offerStatus, setOfferStatus] = useState('pending');
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [chatOffer, setChatOffer] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  const [catalogOverrides, setCatalogOverrides] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [savingPrice, setSavingPrice] = useState(null);

  const fetchOffers = useCallback(async () => {
    setOffersLoading(true);
    try {
      const list = await api.listAllOffers(offerStatus);
      setOffers(list);
    } catch (e) {
      toast({ title: 'Failed to load offers', description: e.message });
    } finally {
      setOffersLoading(false);
    }
  }, [offerStatus]);

  const fetchCatalog = useCallback(async () => {
    try {
      const list = await api.getCatalog();
      setCatalogOverrides(list);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!loading && !isAdmin) {
      // Not admin → bounce to home
      navigate('/');
    }
  }, [loading, isAdmin, navigate]);

  useEffect(() => {
    if (isAdmin) {
      fetchOffers();
      fetchCatalog();
    }
  }, [isAdmin, fetchOffers, fetchCatalog]);

  const openChat = (offer) => {
    setChatOffer(offer);
    setChatOpen(true);
  };

  const declineOffer = async (offer) => {
    const reason = window.prompt(
      `Decline this ${offer.sprite_name} offer?\n\nOptional message to the buyer:`,
      ''
    );
    if (reason === null) return; // cancelled
    try {
      const updated = await api.declineOffer(offer.id, { admin_reply: (reason || '').trim() });
      setOffers((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      toast({ title: 'Offer declined', description: `Buyer notified for ${offer.sprite_name}.` });
    } catch (e) {
      toast({ title: 'Failed to decline', description: e.message });
    }
  };

  const deleteOffer = async (offer) => {
    if (!window.confirm(
      `Permanently delete this offer for ${offer.sprite_name}?\n\nThis also removes the chat thread and cannot be undone.`
    )) return;
    try {
      await api.deleteOffer(offer.id);
      setOffers((prev) => prev.filter((o) => o.id !== offer.id));
      toast({ title: 'Offer deleted' });
    } catch (e) {
      toast({ title: 'Failed to delete', description: e.message });
    }
  };

  const handleOfferChange = (updated) => {
    setOffers((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
    setChatOffer(updated);
  };

  const priceMap = useMemo(() => buildPriceMap(catalogOverrides), [catalogOverrides]);

  const filteredSprites = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    if (!q) return SPRITES;
    return SPRITES.filter((s) => (
      s.name.toLowerCase().includes(q) ||
      s.rarity.toLowerCase().includes(q) ||
      s.variant.toLowerCase().includes(q)
    ));
  }, [catalogSearch]);

  const savePrice = async (sprite, price, available) => {
    setSavingPrice(sprite.id);
    try {
      await api.setCatalog(sprite.id, {
        suggested_price: parseFloat(price),
        available,
      });
      await fetchCatalog();
      toast({ title: `${sprite.name} updated` });
    } catch (e) {
      toast({ title: 'Save failed', description: e.message });
    } finally {
      setSavingPrice(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fff7d6] flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[#9d3dff]" />
      </div>
    );
  }
  if (!isAuthed || !isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#fff7d6] pb-20" data-testid="admin-panel">
      {/* Top bar */}
      <header className="bg-[#3a1064] border-b-[3px] border-[#ffd900] sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <button
            data-testid="admin-back-btn"
            onClick={() => navigate('/')}
            className="h-10 w-10 rounded-xl bg-[#ffd900] border-2 border-[#3a1064] flex items-center justify-center text-[#3a1064] shadow-[0_3px_0_#000] hover:bg-yellow-300"
            aria-label="Back"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <Shield className="h-7 w-7 text-[#ffd900]" />
          <h1 className="text-white font-black text-2xl tracking-tight flex-1">
            Admin Panel
          </h1>
          <span className="text-white/80 font-bold text-sm hidden sm:block">
            {user?.email}
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 pt-6">
        <SiteStatsBar />

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList
            data-testid="admin-tabs"
            className="bg-white border-[3px] border-[#3a1064] rounded-xl shadow-[0_4px_0_#3a1064] p-1 h-12"
          >
            <TabsTrigger
              value="offers"
              data-testid="tab-offers"
              className="h-9 px-4 font-black text-[#3a1064] data-[state=active]:bg-[#ff2bd6] data-[state=active]:text-white rounded-lg"
            >
              <Inbox className="h-4 w-4 mr-2" /> Offers
            </TabsTrigger>
            <TabsTrigger
              value="catalog"
              data-testid="tab-catalog"
              className="h-9 px-4 font-black text-[#3a1064] data-[state=active]:bg-[#ff2bd6] data-[state=active]:text-white rounded-lg"
            >
              <Tag className="h-4 w-4 mr-2" /> Catalog
            </TabsTrigger>
            <TabsTrigger
              value="support"
              data-testid="tab-support"
              className="h-9 px-4 font-black text-[#3a1064] data-[state=active]:bg-[#ff2bd6] data-[state=active]:text-white rounded-lg"
            >
              <MessageCircle className="h-4 w-4 mr-2" /> Support
            </TabsTrigger>
            <TabsTrigger
              value="creators"
              data-testid="tab-creators"
              className="h-9 px-4 font-black text-[#3a1064] data-[state=active]:bg-[#ff2bd6] data-[state=active]:text-white rounded-lg"
            >
              <Video className="h-4 w-4 mr-2" /> Creators
            </TabsTrigger>
            <TabsTrigger
              value="users"
              data-testid="tab-users"
              className="h-9 px-4 font-black text-[#3a1064] data-[state=active]:bg-[#ff2bd6] data-[state=active]:text-white rounded-lg"
            >
              <Users className="h-4 w-4 mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger
              value="trades"
              data-testid="tab-trades"
              className="h-9 px-4 font-black text-[#3a1064] data-[state=active]:bg-[#ff2bd6] data-[state=active]:text-white rounded-lg"
            >
              <Handshake className="h-4 w-4 mr-2" /> Trades
            </TabsTrigger>
            <TabsTrigger
              value="lottery"
              data-testid="tab-lottery"
              className="h-9 px-4 font-black text-[#3a1064] data-[state=active]:bg-[#ff2bd6] data-[state=active]:text-white rounded-lg"
            >
              <Trophy className="h-4 w-4 mr-2" /> Lottery
            </TabsTrigger>
            <TabsTrigger
              value="site"
              data-testid="tab-site"
              className="h-9 px-4 font-black text-[#3a1064] data-[state=active]:bg-[#ff2bd6] data-[state=active]:text-white rounded-lg"
            >
              <Settings className="h-4 w-4 mr-2" /> Site
            </TabsTrigger>
            <TabsTrigger
              value="blog"
              data-testid="tab-blog"
              className="h-9 px-4 font-black text-[#3a1064] data-[state=active]:bg-[#ff2bd6] data-[state=active]:text-white rounded-lg"
            >
              <Newspaper className="h-4 w-4 mr-2" /> Blog
            </TabsTrigger>
            <TabsTrigger
              value="merch"
              data-testid="tab-merch"
              className="h-9 px-4 font-black text-[#3a1064] data-[state=active]:bg-[#ff2bd6] data-[state=active]:text-white rounded-lg"
            >
              <ShoppingBag className="h-4 w-4 mr-2" /> Merch
            </TabsTrigger>
          </TabsList>

          {/* OFFERS TAB */}
          <TabsContent value="offers" className="mt-6 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              {STATUS_TABS.map((s) => (
                <button
                  key={s}
                  data-testid={`status-filter-${s}`}
                  onClick={() => setOfferStatus(s)}
                  className={`h-10 px-4 rounded-xl border-2 border-[#3a1064] font-black text-xs uppercase tracking-wider shadow-[0_3px_0_#3a1064] transition-colors ${
                    offerStatus === s
                      ? 'bg-[#3a1064] text-white'
                      : 'bg-white text-[#3a1064] hover:bg-yellow-50'
                  }`}
                >
                  {s === 'pending' && <Clock className="h-3.5 w-3.5 inline mr-1" />}
                  {s === 'accepted' && <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />}
                  {s === 'declined' && <XCircle className="h-3.5 w-3.5 inline mr-1" />}
                  {s}
                </button>
              ))}
              <Button
                data-testid="refresh-offers-btn"
                variant="outline"
                onClick={fetchOffers}
                className="h-10 rounded-xl border-2 border-[#3a1064] bg-white text-[#3a1064] font-black"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${offersLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <span className="ml-auto font-black text-[#3a1064] text-sm">
                {offers.length} offer{offers.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="space-y-3">
              {offersLoading && offers.length === 0 ? (
                <div className="py-12 text-center"><Loader2 className="h-8 w-8 mx-auto animate-spin text-[#9d3dff]" /></div>
              ) : offers.length === 0 ? (
                <div className="py-12 text-center text-[#3a1064]/70 font-bold">
                  No {offerStatus} offers.
                </div>
              ) : (
                offers.map((o) => (
                  <div
                    key={o.id}
                    data-testid={`admin-offer-${o.id}`}
                    className="w-full text-left bg-white rounded-2xl border-2 border-[#3a1064] shadow-[0_4px_0_#3a1064] p-4 hover:bg-yellow-50 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <img src={o.sprite_img} alt="" className="h-20 w-20 object-contain bg-yellow-50 rounded-xl border-2 border-[#3a1064] p-1 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <div>
                            <div className="font-black text-[#3a1064] text-lg">{o.sprite_name}</div>
                            <div className="flex gap-1 mt-1 flex-wrap">
                              <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded text-white" style={{ background: (RARITIES[o.sprite_rarity] || {}).color || '#3a1064' }}>{o.sprite_rarity}</span>
                              <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded text-white" style={{ background: (VARIANTS[o.sprite_variant] || {}).color || '#9d3dff' }}>{o.sprite_variant}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-black text-[#3a1064] text-2xl flex items-center gap-0.5">
                              <DollarSign className="h-5 w-5" />{o.offer_amount.toFixed(2)}
                            </div>
                            <div className="text-[10px] font-bold text-[#3a1064]/60 uppercase tracking-wider">
                              Suggested ${o.suggested_price.toFixed(2)}
                            </div>
                          </div>
                        </div>

                        <div className="text-xs font-bold text-[#3a1064]/80 mt-2">
                          From <span className="text-[#3a1064] font-black">{o.buyer_name || o.buyer_email}</span>
                          {o.buyer_email ? <span className="text-[#3a1064]/60"> · {o.buyer_email}</span> : null}
                        </div>
                        <div className="text-[10px] font-bold text-[#3a1064]/50">{fmtDate(o.created_at)}</div>

                        {o.message && (
                          <div className="mt-2 bg-yellow-50 border-2 border-[#ffd900] rounded-lg px-3 py-2 text-xs font-bold text-[#3a1064] italic">
                            &ldquo;{o.message}&rdquo;
                          </div>
                        )}

                        {o.status === 'accepted' && (
                          <div className="mt-3 bg-emerald-50 border-2 border-emerald-300 rounded-lg p-2 text-xs">
                            <div className="font-black text-emerald-700 uppercase text-[10px] tracking-wider">Accepted on {fmtDate(o.responded_at)}</div>
                            {o.payment_link && (
                              <div className="text-[#3a1064] font-black mt-1 inline-flex items-center gap-1 break-all">
                                <ExternalLink className="h-3 w-3 shrink-0" /> {o.payment_link}
                              </div>
                            )}
                            {o.meet_up_note && <div className="text-[#3a1064] font-bold mt-1">Meet-up: {o.meet_up_note}</div>}
                          </div>
                        )}
                        {o.status === 'declined' && o.admin_reply && (
                          <div className="mt-3 bg-rose-50 border-2 border-rose-300 rounded-lg p-2 text-xs font-bold text-[#3a1064]">
                            Reply: {o.admin_reply}
                          </div>
                        )}

                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            data-testid={`open-chat-${o.id}`}
                            onClick={() => openChat(o)}
                            className="h-8 px-3 rounded-lg bg-[#ff2bd6] hover:bg-[#e119bf] text-white border-2 border-[#3a1064] font-black text-xs shadow-[0_3px_0_#3a1064] inline-flex items-center gap-1"
                          >
                            <MessageSquare className="h-3.5 w-3.5" /> Open chat
                          </button>
                          {o.status === 'pending' && (
                            <button
                              type="button"
                              data-testid={`decline-offer-${o.id}`}
                              onClick={() => declineOffer(o)}
                              className="h-8 px-3 rounded-lg bg-white hover:bg-rose-50 text-rose-700 border-2 border-rose-500 font-black text-xs inline-flex items-center gap-1"
                            >
                              <XCircle className="h-3.5 w-3.5" /> Decline
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid={`delete-offer-${o.id}`}
                            onClick={() => deleteOffer(o)}
                            className="ml-auto h-8 px-3 rounded-lg bg-white hover:bg-rose-50 text-rose-700 border-2 border-rose-500 font-black text-xs inline-flex items-center gap-1"
                            title="Permanently delete this offer + chat thread"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          {/* CATALOG TAB */}
          <TabsContent value="catalog" className="mt-6 space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3a1064]/50" />
                <Input
                  data-testid="catalog-search-input"
                  value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  placeholder="Search sprite name, rarity, variant…"
                  className="pl-10 h-11 rounded-xl border-2 border-[#3a1064] font-bold text-[#3a1064]"
                />
              </div>
              <span className="font-black text-[#3a1064] text-sm">
                {filteredSprites.length} sprite{filteredSprites.length !== 1 ? 's' : ''}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredSprites.map((s) => (
                <CatalogRow
                  key={s.id}
                  sprite={s}
                  priceInfo={priceMap[s.id]}
                  saving={savingPrice === s.id}
                  onSave={(price, available) => savePrice(s, price, available)}
                />
              ))}
            </div>
          </TabsContent>

          {/* SUPPORT TAB */}
          <TabsContent value="support" className="mt-6">
            <SupportChatPanel active={tab === 'support'} />
          </TabsContent>

          {/* TRADES TAB */}
          <TabsContent value="trades" className="mt-6">
            <TradesAdminPanel active={tab === 'trades'} />
          </TabsContent>

          {/* CREATORS TAB */}
          <TabsContent value="creators" className="mt-6">
            <CreatorsAdminPanel active={tab === 'creators'} />
          </TabsContent>

          {/* USERS TAB */}
          <TabsContent value="users" className="mt-6">
            <UsersAdminPanel active={tab === 'users'} />
          </TabsContent>

          <TabsContent value="lottery" className="mt-6">
            <LotteryAdminPanel active={tab === 'lottery'} />
          </TabsContent>

          <TabsContent value="site" className="mt-6">
            <SiteSettingsAdminCard active={tab === 'site'} />
          </TabsContent>

          <TabsContent value="blog" className="mt-6">
            <BlogAdminCard active={tab === 'blog'} />
          </TabsContent>

          <TabsContent value="merch" className="mt-6">
            <MerchAdminCard active={tab === 'merch'} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Offer chat dialog (replaces old accept/decline modal) */}
      <OfferChatDialog
        open={chatOpen}
        onOpenChange={(v) => { setChatOpen(v); if (!v) { fetchOffers(); } }}
        offer={chatOffer}
        onOfferChange={handleOfferChange}
      />
    </div>
  );
}

// ─── Catalog row ─────────────────────────────────────────────────────────────
function CatalogRow({ sprite, priceInfo, saving, onSave }) {
  const initial = priceInfo?.price ?? defaultPrice(sprite);
  const [price, setPrice] = useState(String(initial));
  const [available, setAvailable] = useState(priceInfo?.available !== false);
  const isOverride = !!priceInfo?.override;

  useEffect(() => {
    setPrice(String(priceInfo?.price ?? defaultPrice(sprite)));
    setAvailable(priceInfo?.available !== false);
  }, [priceInfo, sprite]);

  const r = RARITIES[sprite.rarity];
  const v = VARIANTS[sprite.variant];

  return (
    <div
      data-testid={`catalog-row-${sprite.id}`}
      className="bg-white rounded-xl border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] p-3 flex items-center gap-3"
    >
      <img src={sprite.img} alt="" className="h-14 w-14 object-contain bg-yellow-50 rounded-lg border-2 border-[#3a1064] p-1 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-black text-[#3a1064] text-sm truncate">{sprite.name}</div>
        <div className="flex gap-1 mt-1 flex-wrap">
          <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded text-white" style={{ background: r.color }}>{r.label}</span>
          <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded text-white" style={{ background: v.color }}>{v.label}</span>
          {isOverride ? (
            <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-[#3a1064] text-white">CUSTOM</span>
          ) : (
            <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-yellow-200 text-[#3a1064]">DEFAULT</span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5">
        <div className="flex items-center gap-1">
          <span className="text-[#3a1064] font-black text-sm">$</span>
          <Input
            data-testid={`catalog-price-${sprite.id}`}
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-20 h-9 rounded-lg border-2 border-[#3a1064] font-black text-[#3a1064] text-sm text-right"
          />
        </div>
        <label className="text-[10px] font-black text-[#3a1064] flex items-center gap-1 cursor-pointer">
          <input
            type="checkbox"
            data-testid={`catalog-available-${sprite.id}`}
            checked={available}
            onChange={(e) => setAvailable(e.target.checked)}
            className="h-3 w-3 accent-[#ff2bd6]"
          />
          Available
        </label>
        <Button
          data-testid={`catalog-save-${sprite.id}`}
          onClick={() => onSave(price, available)}
          disabled={saving}
          className="h-8 px-2 rounded-lg border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black text-xs shadow-[0_2px_0_#3a1064]"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Save className="h-3 w-3 mr-1" /> Save</>}
        </Button>
      </div>
    </div>
  );
}

// ─── Support Chat Panel ─────────────────────────────────────────────────────
function SupportChatPanel({ active }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = React.useRef(null);

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listSupportThreads();
      setThreads(list);
    } catch (e) {
      toast({ title: 'Failed to load threads', description: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (key) => {
    if (!key) return;
    setMsgLoading(true);
    try {
      const msgs = await api.getSupportThread(key);
      setMessages(msgs || []);
      await api.markSupportRead(key);
      loadThreads();
    } catch (e) {
      toast({ title: 'Failed to load messages', description: e.message });
    } finally {
      setMsgLoading(false);
    }
  }, [loadThreads]);

  useEffect(() => {
    if (active) loadThreads();
  }, [active, loadThreads]);

  useEffect(() => {
    if (!active) return;
    const t = setInterval(loadThreads, 20000);
    return () => clearInterval(t);
  }, [active, loadThreads]);

  useEffect(() => {
    if (selectedKey) loadMessages(selectedKey);
  }, [selectedKey, loadMessages]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const reply = async () => {
    const v = replyText.trim();
    if (!v || !selectedKey) return;
    setSending(true);
    try {
      const msg = await api.replySupportThread(selectedKey, v);
      setMessages((prev) => [...prev, msg]);
      setReplyText('');
      loadThreads();
    } catch (e) {
      toast({ title: 'Reply failed', description: e.message });
    } finally {
      setSending(false);
    }
  };

  const selectedThread = threads.find((t) => t.thread_key === selectedKey);

  return (
    <div className="bg-white border-[3px] border-[#3a1064] rounded-2xl shadow-[0_5px_0_#3a1064] overflow-hidden">
      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] min-h-[500px]">
        {/* Threads list */}
        <div className="border-b-[3px] md:border-b-0 md:border-r-[3px] border-[#3a1064] bg-yellow-50 flex flex-col max-h-[600px]">
          <div className="p-3 border-b-2 border-[#3a1064]/15 bg-[#ffd900] flex items-center justify-between">
            <div className="font-black text-[#3a1064] text-sm flex items-center gap-1.5">
              <Inbox className="h-4 w-4" /> Threads ({threads.length})
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={loadThreads}
              disabled={loading}
              className="h-7 px-2 rounded-lg border-2 border-[#3a1064] bg-white font-black text-xs"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            </Button>
          </div>
          <div className="overflow-y-auto flex-1">
            {threads.length === 0 && !loading && (
              <div className="text-center text-[#3a1064]/60 font-bold text-sm py-10 px-4">
                No support messages yet.
              </div>
            )}
            {threads.map((t) => {
              const isSelected = t.thread_key === selectedKey;
              return (
                <button
                  key={t.thread_key}
                  data-testid={`support-thread-${t.thread_key}`}
                  onClick={() => setSelectedKey(t.thread_key)}
                  className={`w-full text-left px-3 py-2.5 border-b border-[#3a1064]/10 transition-colors ${
                    isSelected ? 'bg-[#ff2bd6]/15' : 'hover:bg-yellow-100'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-full border-2 border-[#3a1064] flex items-center justify-center shrink-0 ${t.is_authed ? 'bg-emerald-100' : 'bg-white'}`}>
                      <User className="h-4 w-4 text-[#3a1064]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <div className="font-black text-[#3a1064] text-sm truncate">{t.display_name}</div>
                        {t.unread_from_user > 0 && (
                          <span className="text-[10px] font-black px-1.5 rounded-full bg-[#ff2bd6] text-white shrink-0">
                            {t.unread_from_user}
                          </span>
                        )}
                      </div>
                      {t.email && (
                        <div className="text-[10px] font-bold text-[#3a1064]/70 truncate flex items-center gap-0.5">
                          <Mail className="h-2.5 w-2.5" /> {t.email}
                        </div>
                      )}
                      <div className="text-xs font-bold text-[#3a1064]/70 truncate mt-0.5">{t.last_message}</div>
                      <div className="text-[9px] font-bold text-[#3a1064]/50 mt-0.5">{fmtDate(t.last_message_at)}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Conversation view */}
        <div className="flex flex-col max-h-[600px]">
          {!selectedKey ? (
            <div className="flex-1 flex items-center justify-center text-center px-6 py-12">
              <div>
                <MessageCircle className="h-12 w-12 mx-auto text-[#ff2bd6]" />
                <div className="mt-3 font-black text-[#3a1064] text-lg">Pick a thread</div>
                <div className="text-sm font-bold text-[#3a1064]/70 mt-1">
                  Choose a conversation on the left to read and reply.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="p-3 border-b-2 border-[#3a1064]/15 bg-yellow-50 flex items-center gap-2 min-w-0">
                <div className={`h-9 w-9 rounded-full border-2 border-[#3a1064] flex items-center justify-center shrink-0 ${selectedThread?.is_authed ? 'bg-emerald-100' : 'bg-white'}`}>
                  <User className="h-5 w-5 text-[#3a1064]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-black text-[#3a1064] truncate">{selectedThread?.display_name || 'Anonymous'}</div>
                  <div className="text-xs font-bold text-[#3a1064]/70 truncate">
                    {selectedThread?.email || (selectedThread?.is_authed ? 'Signed-in user' : 'No email provided')}
                    {' · '}{selectedThread?.total_messages || 0} messages
                  </div>
                </div>
              </div>
              <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#fff7d6]">
                {msgLoading && messages.length === 0 && (
                  <div className="text-center py-6"><Loader2 className="h-5 w-5 mx-auto animate-spin text-[#9d3dff]" /></div>
                )}
                {messages.map((m) => {
                  const isAdmin = m.sender_type === 'admin';
                  return (
                    <div key={m.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 border-2 shadow-[0_2px_0_#3a1064] text-sm font-bold whitespace-pre-wrap break-words ${
                        isAdmin
                          ? 'bg-[#ff2bd6] text-white border-[#3a1064] rounded-tr-sm'
                          : 'bg-white text-[#3a1064] border-[#3a1064] rounded-tl-sm'
                      }`}>
                        <div className={`flex items-center gap-1 text-[10px] font-black uppercase tracking-wider mb-0.5 ${isAdmin ? 'text-yellow-200' : 'text-[#9d3dff]'}`}>
                          {isAdmin ? <ShieldCheck className="h-3 w-3" /> : <User className="h-3 w-3" />}
                          {isAdmin ? 'You (Dev)' : (m.sender_name || 'User')}
                          <span className="ml-1 text-[9px] font-bold text-current opacity-70">{fmtDate(m.created_at)}</span>
                        </div>
                        <div>{m.text}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-3 border-t-[3px] border-[#3a1064] bg-white flex items-end gap-2">
                <textarea
                  data-testid="admin-support-reply-input"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); reply(); } }}
                  placeholder="Type your reply…"
                  rows={1}
                  maxLength={2000}
                  className="flex-1 min-h-[40px] max-h-[120px] rounded-xl border-2 border-[#3a1064] px-3 py-2 font-bold text-[#3a1064] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#ff2bd6]/40"
                />
                <Button
                  data-testid="admin-support-send-btn"
                  onClick={reply}
                  disabled={sending || !replyText.trim()}
                  className="h-10 rounded-xl border-2 border-[#3a1064] bg-[#ff2bd6] hover:bg-[#e119bf] disabled:opacity-40 text-white font-black shadow-[0_3px_0_#3a1064] px-4"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Reply</>}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}



// ─── Creators Admin Panel ────────────────────────────────────────────────────
function CreatorsAdminPanel({ active }) {
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listCreatorSubmissions(filter === 'all' ? '' : filter);
      setSubs(list || []);
    } catch (e) {
      toast({ title: 'Failed to load', description: e.message });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const review = async (sub, action) => {
    setBusy(sub.id);
    try {
      const fn = action === 'approve' ? api.approveCreator : api.rejectCreator;
      const updated = await fn(sub.id);
      setSubs((prev) => prev.map((s) => (s.id === sub.id ? updated : s)));
      toast({
        title: action === 'approve' ? `+${100} entries awarded` : 'Submission rejected',
        description: action === 'approve' ? `${sub.user_display} just got 100 bonus lottery entries.` : undefined,
      });
    } catch (e) {
      toast({ title: 'Action failed', description: e.message });
    } finally {
      setBusy('');
    }
  };

  const FILTERS = ['pending', 'approved', 'rejected', 'all'];

  return (
    <div className="bg-white border-[3px] border-[#3a1064] rounded-2xl shadow-[0_5px_0_#3a1064] overflow-hidden">
      <div className="p-3 sm:p-4 border-b-2 border-[#3a1064]/15 bg-[#ffd900] flex items-center justify-between gap-2 flex-wrap">
        <div className="font-black text-[#3a1064] text-sm flex items-center gap-1.5">
          <Video className="h-4 w-4" /> Creator submissions
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`creators-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`h-7 px-2.5 rounded-lg text-[11px] font-black uppercase border-2 border-[#3a1064] transition ${
                filter === f ? 'bg-[#3a1064] text-yellow-300' : 'bg-white text-[#3a1064]'
              }`}
            >
              {f}
            </button>
          ))}
          <button
            onClick={load}
            disabled={loading}
            className="h-7 w-7 rounded-lg border-2 border-[#3a1064] bg-white text-[#3a1064] flex items-center justify-center"
            aria-label="Refresh"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </button>
        </div>
      </div>

      <div className="p-3 sm:p-4 space-y-2 min-h-[300px]">
        {loading && subs.length === 0 && (
          <div className="text-center py-10"><Loader2 className="h-6 w-6 mx-auto animate-spin text-[#9d3dff]" /></div>
        )}
        {!loading && subs.length === 0 && (
          <div className="text-center py-10 text-[#3a1064]/60 font-bold text-sm">
            No submissions in this list.
          </div>
        )}
        {subs.map((s) => {
          const isBusy = busy === s.id;
          return (
            <div key={s.id} className="rounded-xl border-2 border-[#3a1064]/20 p-3 bg-yellow-50 flex items-start gap-3 flex-wrap sm:flex-nowrap shadow-[0_2px_0_rgba(58,16,100,0.08)]">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-black text-[#3a1064]">{s.user_display}</span>
                  <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-[#3a1064] text-yellow-300 border-2 border-yellow-300">
                    {s.platform}
                  </span>
                  {s.status === 'approved' && (
                    <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-500 text-white border-2 border-[#0c5132]">
                      <Ticket className="h-2.5 w-2.5 inline mr-0.5" />+{s.entries_awarded}
                    </span>
                  )}
                  {s.status === 'rejected' && (
                    <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-rose-500 text-white border-2 border-rose-800">
                      Rejected
                    </span>
                  )}
                </div>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 text-xs font-bold text-[#3a1064] hover:text-[#ff2bd6] flex items-center gap-1 truncate"
                >
                  <ExternalLink className="h-3 w-3 shrink-0" /> <span className="truncate">{s.url}</span>
                </a>
                {s.note && <div className="mt-1 text-[11px] font-bold text-[#3a1064]/70">User note: {s.note}</div>}
                <div className="mt-1 text-[10px] font-bold text-[#3a1064]/50">{fmtDate(s.created_at)}</div>
              </div>
              {s.status === 'pending' && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    data-testid={`approve-creator-${s.id}`}
                    onClick={() => review(s, 'approve')}
                    disabled={isBusy}
                    className="h-9 px-3 rounded-lg border-2 border-[#3a1064] bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 text-white font-black text-xs shadow-[0_2px_0_#3a1064] flex items-center gap-1"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                  </button>
                  <button
                    data-testid={`reject-creator-${s.id}`}
                    onClick={() => review(s, 'reject')}
                    disabled={isBusy}
                    className="h-9 px-3 rounded-lg border-2 border-[#3a1064] bg-rose-500 hover:bg-rose-600 disabled:opacity-40 text-white font-black text-xs shadow-[0_2px_0_#3a1064] flex items-center gap-1"
                  >
                    <XCircle className="h-3.5 w-3.5" /> Reject
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Users Admin Panel ──────────────────────────────────────────────────────
function UsersAdminPanel({ active }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.adminListUsers();
      setUsers(list || []);
    } catch (e) {
      toast({ title: 'Failed to load users', description: e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (active) load(); }, [active, load]);

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const s = `${u.email || ''} ${u.name || ''} ${u.username || ''}`.toLowerCase();
    return s.includes(q.trim().toLowerCase());
  });

  const totals = users.reduce(
    (acc, u) => ({
      count: acc.count + 1,
      admins: acc.admins + (u.role === 'admin' ? 1 : 0),
      trades: acc.trades + (u.trade_count || 0),
    }),
    { count: 0, admins: 0, trades: 0 },
  );

  return (
    <div className="bg-white border-[3px] border-[#3a1064] rounded-2xl shadow-[0_5px_0_#3a1064] overflow-hidden">
      {/* Header */}
      <div className="p-3 sm:p-4 border-b-2 border-[#3a1064]/15 bg-[#ffd900] flex items-center justify-between gap-2 flex-wrap">
        <div className="font-black text-[#3a1064] text-sm flex items-center gap-1.5">
          <Users className="h-4 w-4" /> Signed-up users ({users.length})
        </div>
        <div className="flex items-center gap-2 flex-1 sm:flex-none min-w-0">
          <div className="relative flex-1 sm:w-56">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#3a1064]/50" />
            <input
              data-testid="users-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or name…"
              className="w-full h-8 pl-8 pr-2 rounded-lg border-2 border-[#3a1064] bg-white text-[#3a1064] font-bold text-xs focus:outline-none"
            />
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="h-8 w-8 rounded-lg border-2 border-[#3a1064] bg-white text-[#3a1064] flex items-center justify-center shrink-0"
            aria-label="Refresh"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div className="p-3 sm:p-4 grid grid-cols-3 gap-2 sm:gap-3 border-b-2 border-[#3a1064]/10 bg-yellow-50">
        <SummaryCard label="Users" value={totals.count} accent="#ffd900" fg="#3a1064" />
        <SummaryCard label="Admins" value={totals.admins} accent="#ff2bd6" fg="#fff" />
        <SummaryCard label="Trades total" value={totals.trades} accent="#00b8ff" fg="#fff" />
      </div>

      {/* User rows */}
      <div className="p-3 sm:p-4 space-y-2 min-h-[240px]">
        {loading && users.length === 0 && (
          <div className="text-center py-8"><Loader2 className="h-6 w-6 mx-auto animate-spin text-[#9d3dff]" /></div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-8 text-[#3a1064]/60 font-bold text-sm">No users match your search.</div>
        )}
        {filtered.map((u) => (
          <div
            key={u.id}
            data-testid={`user-row-${u.id}`}
            className="rounded-xl border-2 border-[#3a1064]/15 p-2.5 sm:p-3 bg-white flex items-start gap-2.5 flex-wrap sm:flex-nowrap shadow-[0_2px_0_rgba(58,16,100,0.06)]"
          >
            <div
              className={`h-10 w-10 rounded-xl border-2 border-[#3a1064] flex items-center justify-center text-white font-black text-sm shrink-0 ${
                u.role === 'admin' ? 'bg-[#ffd900] text-[#3a1064]' : 'bg-[#ff2bd6]'
              }`}
            >
              {((u.name || u.email || '?')[0] || '?').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-black text-[#3a1064] truncate">
                  {u.name || u.username || u.email || 'Unknown'}
                </span>
                {u.role === 'admin' && (
                  <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-yellow-300 text-[#3a1064] border border-[#3a1064]">
                    Admin
                  </span>
                )}
                {u.provider && u.provider !== 'email' && (
                  <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-[#00b8ff] text-white border border-[#0070a0]">
                    {u.provider}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs font-bold text-[#3a1064]/80 mt-0.5 min-w-0">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="truncate">{u.email || '—'}</span>
              </div>
              {u.username && (
                <div className="text-[10px] font-bold text-[#3a1064]/60 mt-0.5">@{u.username}</div>
              )}
              {u.created_at && (
                <div className="text-[10px] font-bold text-[#3a1064]/50 mt-0.5">Joined {fmtDate(u.created_at)}</div>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
              <span
                className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-1 rounded-full bg-[#3a1064] text-yellow-300 border-2 border-yellow-300"
                title={`Sent ${u.trades_sent}, Received ${u.trades_received}`}
              >
                <Handshake className="h-3 w-3" /> {u.trade_count} trades
              </span>
              {u.bonus_entries > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-1 rounded-full bg-emerald-500 text-white border-2 border-[#0c5132]">
                  <Ticket className="h-3 w-3" /> +{u.bonus_entries}
                </span>
              )}
              <GrantEntriesButton user={u} onGranted={load} />
              {u.role !== 'admin' && (
                <DeleteUserButton user={u} onDeleted={load} />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent, fg }) {
  return (
    <div
      className="rounded-lg sm:rounded-xl p-2 sm:p-3 border-2 shadow-[0_2px_0_#3a1064]"
      style={{ background: accent, color: fg, borderColor: '#3a1064' }}
    >
      <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider opacity-90">{label}</div>
      <div className="font-black text-lg sm:text-2xl leading-none mt-0.5">{value}</div>
    </div>
  );
}

// ─── Grant Entries Button (inline mini form) ────────────────────────────────
function DeleteUserButton({ user, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm(
      `Permanently delete ${user.username ? '@' + user.username : user.email}?\n\n` +
      `This wipes their account, collection, trades, community sprites, offers, ` +
      `and notifications. This cannot be undone.`
    )) return;
    setBusy(true);
    try {
      const res = await api.adminDeleteUser(user.id);
      const removedCount = Object.values(res?.cascade || {}).reduce(
        (n, v) => n + (typeof v === 'number' ? v : 0), 0
      );
      toast({
        title: 'User deleted',
        description: removedCount
          ? `Cleaned up ${removedCount} related record${removedCount === 1 ? '' : 's'}.`
          : `${user.username || user.email} is gone.`,
      });
      onDeleted?.();
    } catch (e) {
      toast({ title: 'Failed to delete', description: e.message });
    } finally { setBusy(false); }
  };
  return (
    <button
      data-testid={`delete-user-btn-${user.id}`}
      onClick={remove}
      disabled={busy}
      className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-1 rounded-full bg-white text-rose-700 border-2 border-rose-500 hover:bg-rose-50 disabled:opacity-50"
      title="Delete this user permanently"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
      Delete
    </button>
  );
}


function GrantEntriesButton({ user, onGranted }) {
  const [open, setOpen] = useState(false);
  const [delta, setDelta] = useState(1);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (sign) => {
    const raw = Number(delta);
    if (!Number.isFinite(raw) || raw <= 0) {
      toast({ title: 'Enter a positive number' });
      return;
    }
    setBusy(true);
    try {
      const res = await api.grantEntries(user.id, { delta: sign * raw, note: note.trim() || undefined });
      toast({
        title: `${sign > 0 ? 'Added' : 'Removed'} ${raw} ticket${raw === 1 ? '' : 's'}`,
        description: `${user.username || user.email} now has ${res.total_entries} total entries.`,
      });
      setOpen(false);
      setDelta(1);
      setNote('');
      onGranted?.();
    } catch (e) {
      toast({ title: 'Failed to update entries', description: e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        data-testid={`grant-entries-btn-${user.id}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-1 rounded-full bg-yellow-300 text-[#3a1064] border-2 border-[#3a1064] hover:bg-yellow-400"
        title="Grant lottery tickets"
      >
        <Ticket className="h-3 w-3" /> Grant
      </button>
    );
  }

  return (
    <div className="w-full mt-2 rounded-xl border-2 border-[#3a1064] bg-yellow-50 p-2.5 flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-1">
        <input
          data-testid={`grant-entries-input-${user.id}`}
          type="number"
          min={1}
          max={10000}
          value={delta}
          onChange={(e) => setDelta(e.target.value)}
          className="h-8 w-20 px-2 rounded-lg border-2 border-[#3a1064] font-black text-[#3a1064] text-sm focus:outline-none"
        />
        <span className="text-[10px] font-black text-[#3a1064]/70">tickets</span>
      </div>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Note (optional)"
        className="h-8 px-2 rounded-lg border-2 border-[#3a1064]/50 font-bold text-[#3a1064] text-xs bg-white min-w-[120px] flex-1 focus:outline-none"
      />
      <div className="flex items-center gap-1">
        <button
          data-testid={`grant-entries-add-${user.id}`}
          disabled={busy}
          onClick={() => submit(1)}
          className="h-8 px-2 rounded-lg bg-emerald-500 text-white border-2 border-[#0c5132] font-black text-xs flex items-center gap-1 disabled:opacity-50"
        >
          <Plus className="h-3 w-3" /> Add
        </button>
        <button
          data-testid={`grant-entries-remove-${user.id}`}
          disabled={busy}
          onClick={() => submit(-1)}
          className="h-8 px-2 rounded-lg bg-rose-500 text-white border-2 border-[#7f1d1d] font-black text-xs flex items-center gap-1 disabled:opacity-50"
        >
          <Minus className="h-3 w-3" /> Remove
        </button>
        <button
          onClick={() => { setOpen(false); setDelta(1); setNote(''); }}
          className="h-8 px-2 rounded-lg bg-white text-[#3a1064] border-2 border-[#3a1064] font-black text-xs"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


// ─── Lottery Admin Panel ────────────────────────────────────────────────────
function toLocalInputValue(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    // format YYYY-MM-DDTHH:mm in local time for <input type="datetime-local">
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

function LotteryAdminPanel({ active }) {
  const [settings, setSettings] = useState(null);
  const [drawAt, setDrawAt] = useState('');
  const [prize, setPrize] = useState(13500);
  const [savingSettings, setSavingSettings] = useState(false);

  const [leaderboard, setLeaderboard] = useState({ pool: 0, top: [] });
  const [leaderLoading, setLeaderLoading] = useState(false);

  const [drawCount, setDrawCount] = useState(3);
  const [drawNote, setDrawNote] = useState('');
  const [picking, setPicking] = useState(false);
  const [lastDraw, setLastDraw] = useState(null);

  // Animated wheel state — driven step-by-step from pickWinners()
  const [wheel, setWheel] = useState({
    open: false,
    phase: 'idle',       // 'spinning' | 'reveal' | 'done'
    winners: [],
    targetCount: 3,
    pool: [],
    targetUserId: null,
    spinKey: 0,
  });

  const [draws, setDraws] = useState([]);
  const [drawsLoading, setDrawsLoading] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const s = await api.getLotterySettings();
      setSettings(s);
      setDrawAt(toLocalInputValue(s.draw_at));
      setPrize(s.weekly_prize || 13500);
    } catch (e) {
      toast({ title: 'Failed to load settings', description: e.message });
    }
    setLeaderLoading(true);
    try {
      const lb = await api.getLotteryLeaderboard(25);
      setLeaderboard(lb || { pool: 0, top: [] });
    } catch (e) {
      toast({ title: 'Failed to load leaderboard', description: e.message });
    } finally { setLeaderLoading(false); }
    setDrawsLoading(true);
    try {
      const d = await api.listLotteryDraws(20);
      setDraws(d || []);
    } catch (e) {
      toast({ title: 'Failed to load past draws', description: e.message });
    } finally { setDrawsLoading(false); }
  }, []);

  useEffect(() => { if (active) loadAll(); }, [active, loadAll]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const iso = drawAt ? new Date(drawAt).toISOString() : null;
      const body = { weekly_prize: Number(prize) || 0 };
      if (iso) body.draw_at = iso;
      const s = await api.updateLotterySettings(body);
      setSettings(s);
      toast({ title: 'Lottery settings saved', description: 'Countdown will update everywhere.' });
    } catch (e) {
      toast({ title: 'Failed to save settings', description: e.message });
    } finally { setSavingSettings(false); }
  };

  const pickWinners = async () => {
    setPicking(true);
    setLastDraw(null);

    const target = Math.max(1, Math.min(20, Number(drawCount) || 3));

    // Fetch the FULL eligible pool so every ticketed user shows on the wheel
    // (leaderboard displayed on the card stays capped at 25).
    let fullPool = [];
    try {
      const fullLb = await api.getLotteryLeaderboard(500);
      fullPool = fullLb?.top || [];
    } catch { fullPool = leaderboard.top || []; }

    setWheel({
      open: true,
      phase: 'spinning',
      winners: [],
      targetCount: target,
      pool: fullPool,
      targetUserId: null,
      spinKey: 0,
    });

    try {
      const res = await api.pickLotteryWinners({
        count: target,
        note: drawNote.trim() || undefined,
      });

      // Reveal each winner one-by-one with a fresh spin
      let pool = fullPool.slice();
      for (let i = 0; i < res.winners.length; i++) {
        const w = res.winners[i];
        // Guarantee the winner is in the visible pool
        if (!pool.find((p) => p.user_id === w.user_id)) {
          pool = [...pool, {
            user_id: w.user_id,
            username: w.username,
            name: w.name,
            email: w.email,
            total_entries: w.entries,
          }];
        }
        setWheel((prev) => ({
          ...prev,
          phase: 'spinning',
          pool,
          targetUserId: w.user_id,
          spinKey: prev.spinKey + 1,
        }));
        // Spin duration (matches CSS transition on wheel)
        await new Promise((r) => setTimeout(r, 4600));
        // Reveal + confetti
        setWheel((prev) => ({
          ...prev,
          phase: 'reveal',
          winners: [...prev.winners, w],
        }));
        await new Promise((r) => setTimeout(r, 1500));
      }

      setWheel((prev) => ({ ...prev, phase: 'done' }));
      setLastDraw(res);
      setDrawNote('');
      const d = await api.listLotteryDraws(20);
      setDraws(d || []);
      toast({
        title: `Picked ${res.winners.length} winner${res.winners.length === 1 ? '' : 's'}`,
        description: `From ${res.total_entries} entries · ${res.pool_size} players`,
      });
    } catch (e) {
      setWheel((prev) => ({ ...prev, open: false }));
      toast({ title: 'Failed to pick winner', description: e.message });
    } finally { setPicking(false); }
  };

  return (
    <div className="space-y-4">
      {/* Countdown + prize settings */}
      <div className="bg-white border-[3px] border-[#3a1064] rounded-2xl shadow-[0_5px_0_#3a1064] overflow-hidden">
        <div className="p-3 sm:p-4 bg-[#ffd900] border-b-2 border-[#3a1064]/15 flex items-center gap-2">
          <Clock className="h-4 w-4 text-[#3a1064]" />
          <div className="font-black text-[#3a1064] text-sm">Countdown & Prize</div>
        </div>
        <div className="p-3 sm:p-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto] items-end">
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">Next draw (local time)</div>
            <input
              data-testid="lottery-draw-at-input"
              type="datetime-local"
              value={drawAt}
              onChange={(e) => setDrawAt(e.target.value)}
              className="h-10 w-full px-3 rounded-lg border-2 border-[#3a1064] font-black text-[#3a1064] focus:outline-none"
            />
          </label>
          <label className="block">
            <div className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">Weekly prize (V-Bucks)</div>
            <input
              data-testid="lottery-prize-input"
              type="number"
              min={0}
              step={100}
              value={prize}
              onChange={(e) => setPrize(e.target.value)}
              className="h-10 w-full px-3 rounded-lg border-2 border-[#3a1064] font-black text-[#3a1064] focus:outline-none"
            />
          </label>
          <button
            data-testid="lottery-save-settings-btn"
            onClick={saveSettings}
            disabled={savingSettings}
            className="h-10 px-4 rounded-lg bg-[#ff2bd6] text-white border-2 border-[#3a1064] font-black shadow-[0_3px_0_#3a1064] disabled:opacity-50 flex items-center gap-1.5"
          >
            {savingSettings ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </button>
        </div>
        {settings?.updated_at && (
          <div className="px-4 pb-3 text-[10px] font-bold text-[#3a1064]/60">
            Last updated {fmtDate(settings.updated_at)}
          </div>
        )}
      </div>

      {/* Random Winner Picker */}
      <div className="bg-white border-[3px] border-[#3a1064] rounded-2xl shadow-[0_5px_0_#3a1064] overflow-hidden">
        <div className="p-3 sm:p-4 bg-[#ff2bd6] border-b-2 border-[#3a1064]/15 flex items-center gap-2">
          <Dices className="h-4 w-4 text-white" />
          <div className="font-black text-white text-sm">Pick a Winner</div>
        </div>
        <div className="p-3 sm:p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-[auto_1fr_auto] items-end">
            <label className="block">
              <div className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1"># of winners</div>
              <input
                data-testid="lottery-draw-count-input"
                type="number"
                min={1}
                max={20}
                value={drawCount}
                onChange={(e) => setDrawCount(e.target.value)}
                className="h-10 w-24 px-3 rounded-lg border-2 border-[#3a1064] font-black text-[#3a1064] focus:outline-none"
              />
            </label>
            <label className="block">
              <div className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">Draw note (optional)</div>
              <input
                value={drawNote}
                onChange={(e) => setDrawNote(e.target.value)}
                placeholder="e.g., Week 42 grand draw"
                className="h-10 w-full px-3 rounded-lg border-2 border-[#3a1064] font-bold text-[#3a1064] focus:outline-none"
              />
            </label>
            <button
              data-testid="lottery-pick-winners-btn"
              onClick={pickWinners}
              disabled={picking || leaderboard.top.length === 0}
              className="h-10 px-4 rounded-lg bg-[#3a1064] text-yellow-300 border-2 border-[#3a1064] font-black shadow-[0_3px_0_#1a0440] disabled:opacity-50 flex items-center gap-1.5"
            >
              {picking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Dices className="h-4 w-4" />}
              Roll
            </button>
          </div>

          <div className="rounded-xl bg-yellow-50 border-2 border-dashed border-[#3a1064]/30 p-3 text-xs font-bold text-[#3a1064]">
            <span className="font-black">Pool:</span> {leaderboard.pool} total entries ·{' '}
            <span className="font-black">{leaderboard.top.length}</span> eligible player{leaderboard.top.length === 1 ? '' : 's'}
          </div>

          {lastDraw && (
            <div data-testid="lottery-last-draw" className="rounded-xl border-[3px] border-emerald-500 bg-emerald-50 p-3 sm:p-4 space-y-2">
              <div className="font-black text-emerald-800 flex items-center gap-1.5 text-sm">
                <Trophy className="h-4 w-4" /> Just drawn · {fmtDate(lastDraw.drawn_at)}
              </div>
              {lastDraw.winners.map((w) => (
                <div key={w.tier} className="flex items-center gap-2 bg-white rounded-lg border-2 border-emerald-500 p-2">
                  <div className="h-8 w-8 rounded-full bg-[#ffd900] border-2 border-[#3a1064] text-[#3a1064] font-black text-sm flex items-center justify-center shrink-0">
                    {w.tier}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-[#3a1064] truncate">
                      {w.name || w.username || w.email || w.user_id}
                    </div>
                    <div className="text-[10px] font-bold text-[#3a1064]/70 truncate">
                      {w.username && `@${w.username}`}{w.email && ` · ${w.email}`}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] font-black uppercase text-[#3a1064]/60">Entries</div>
                    <div className="font-black text-[#3a1064]">{w.entries}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] font-black uppercase text-[#3a1064]/60">V-Bucks</div>
                    <div className="font-black text-[#ff2bd6]">{w.prize_vbucks.toLocaleString()}</div>
                  </div>
                </div>
              ))}
              <FeatureDrawButton draw={lastDraw} onChanged={(updated) => setLastDraw(updated)} />
            </div>
          )}
        </div>
      </div>

      {/* Live leaderboard */}
      <div className="bg-white border-[3px] border-[#3a1064] rounded-2xl shadow-[0_5px_0_#3a1064] overflow-hidden">
        <div className="p-3 sm:p-4 bg-[#00b8ff] border-b-2 border-[#3a1064]/15 flex items-center justify-between gap-2">
          <div className="font-black text-white text-sm flex items-center gap-2">
            <Ticket className="h-4 w-4" /> Live leaderboard (top {leaderboard.top.length})
          </div>
          <button
            onClick={loadAll}
            className="h-8 w-8 rounded-lg border-2 border-[#3a1064] bg-white text-[#3a1064] flex items-center justify-center"
            aria-label="Refresh"
          >
            {leaderLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          </button>
        </div>
        <div className="p-3 sm:p-4">
          {leaderboard.top.length === 0 && !leaderLoading && (
            <div className="text-center py-6 text-[#3a1064]/60 font-bold text-sm">
              No users have entries yet.
            </div>
          )}
          {leaderboard.top.map((row, i) => (
            <div
              key={row.user_id}
              data-testid={`lottery-leader-row-${row.user_id}`}
              className="flex items-center gap-2 py-2 border-b border-[#3a1064]/10 last:border-none"
            >
              <div className={`h-7 w-7 rounded-full border-2 border-[#3a1064] text-[#3a1064] font-black text-xs flex items-center justify-center shrink-0 ${
                i === 0 ? 'bg-[#ffd900]' : i === 1 ? 'bg-[#d6d6e6]' : i === 2 ? 'bg-[#cd7f32] text-white' : 'bg-white'
              }`}>{i + 1}</div>
              <div className="min-w-0 flex-1">
                <div className="font-black text-[#3a1064] truncate">
                  {row.name || row.username || row.email || row.user_id}
                </div>
                <div className="text-[10px] font-bold text-[#3a1064]/70 truncate">
                  {row.username && `@${row.username}`} · {row.email}
                </div>
              </div>
              <div className="text-right shrink-0 text-xs font-bold text-[#3a1064]/70 hidden sm:block">
                {row.trade_entries} trades · {row.bonus_entries} bonus
              </div>
              <div className="text-right shrink-0">
                <div className="font-black text-[#3a1064]">{row.total_entries}</div>
                <div className="text-[9px] font-black uppercase text-[#3a1064]/60">tickets</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Past draws */}
      <div className="bg-white border-[3px] border-[#3a1064] rounded-2xl shadow-[0_5px_0_#3a1064] overflow-hidden">
        <div className="p-3 sm:p-4 bg-[#3a1064] border-b-2 border-[#3a1064]/15 flex items-center gap-2">
          <Trophy className="h-4 w-4 text-yellow-300" />
          <div className="font-black text-yellow-300 text-sm">Past draws ({draws.length})</div>
        </div>
        <div className="p-3 sm:p-4 space-y-2">
          {drawsLoading && draws.length === 0 && (
            <div className="text-center py-6"><Loader2 className="h-5 w-5 mx-auto animate-spin text-[#9d3dff]" /></div>
          )}
          {!drawsLoading && draws.length === 0 && (
            <div className="text-center py-6 text-[#3a1064]/60 font-bold text-sm">No draws yet — hit Roll above to pick your first winner.</div>
          )}
          {draws.map((d) => (
            <div key={d.id} className={`rounded-xl border-2 p-3 ${d.featured ? 'border-emerald-500 bg-emerald-50' : 'border-[#3a1064]/15 bg-yellow-50'}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-black text-[#3a1064] text-sm flex items-center gap-2">
                  {fmtDate(d.drawn_at)}
                  {d.featured && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-500 text-white border border-emerald-700">
                      Live on homepage
                    </span>
                  )}
                </div>
                <div className="text-[10px] font-bold text-[#3a1064]/70">
                  Pool {d.pool_size} · {d.total_entries} entries · prize {d.weekly_prize.toLocaleString()} V-Bucks
                </div>
              </div>
              {d.note && (
                <div className="mt-1 text-xs font-bold text-[#3a1064]/70 italic">“{d.note}”</div>
              )}
              <div className="mt-2 grid gap-1">
                {d.winners.map((w) => (
                  <div key={`${d.id}-${w.tier}`} className="flex items-center gap-2 bg-white rounded-lg border border-[#3a1064]/20 px-2 py-1 text-xs">
                    <span className="h-5 w-5 rounded-full bg-[#ffd900] text-[#3a1064] border border-[#3a1064] font-black text-[10px] flex items-center justify-center shrink-0">{w.tier}</span>
                    <span className="font-black text-[#3a1064] truncate flex-1">
                      {w.name || w.username || w.email || w.user_id}
                      {w.username && <span className="text-[#3a1064]/60 font-bold"> @{w.username}</span>}
                    </span>
                    <span className="font-black text-[#ff2bd6] shrink-0">{w.prize_vbucks.toLocaleString()} V-Bucks</span>
                  </div>
                ))}
              </div>
              <FeatureDrawButton
                draw={d}
                onChanged={(updated) => setDraws((prev) => prev.map((x) => (
                  x.id === updated.id ? updated : { ...x, featured: false }
                )))}
              />
            </div>
          ))}
        </div>
      </div>

      <LotteryWheelDialog
        open={wheel.open}
        onClose={() => setWheel((prev) => ({ ...prev, open: false }))}
        pool={wheel.pool}
        winners={wheel.winners}
        targetCount={wheel.targetCount}
        phase={wheel.phase}
        spinKey={wheel.spinKey}
        targetUserId={wheel.targetUserId}
      />
    </div>
  );
}




function FeatureDrawButton({ draw, onChanged }) {
  const [busy, setBusy] = useState(false);
  const on = !!draw?.featured;
  const toggle = async () => {
    setBusy(true);
    try {
      const updated = await api.featureLotteryDraw(draw.id, !on);
      onChanged?.(updated);
      toast({
        title: !on ? 'Posted to homepage!' : 'Removed from homepage',
        description: !on
          ? 'Visitors will now see a "Watch the drawing" card on the landing page.'
          : 'The homepage replay card is hidden again.',
      });
    } catch (e) {
      toast({ title: 'Could not update', description: e.message });
    } finally { setBusy(false); }
  };
  return (
    <button
      data-testid={`feature-draw-${draw.id}`}
      onClick={toggle}
      disabled={busy}
      className={`mt-2 w-full h-9 rounded-lg font-black text-xs border-2 flex items-center justify-center gap-1.5 shadow-[0_3px_0_#3a1064] disabled:opacity-50 ${
        on
          ? 'bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-700'
          : 'bg-[#ff2bd6] hover:bg-[#e119bf] text-white border-[#3a1064]'
      }`}
    >
      {busy
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : <Radio className="h-3.5 w-3.5" strokeWidth={3} />}
      {on ? 'Unpin from homepage' : 'Post replay to homepage'}
    </button>
  );
}


// ─── Site-wide feature flags (Site tab) ──────────────────────────────────────
function SiteSettingsAdminCard({ active }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!active) return () => {};
    let alive = true;
    api
      .siteSettings()
      .then((s) => { if (alive) setSettings(s); })
      .catch(() => { if (alive) setSettings({ intro_splash_enabled: true }); });
    return () => { alive = false; };
  }, [active]);

  const toggleIntroSplash = async () => {
    if (!settings || saving) return;
    const next = !settings.intro_splash_enabled;
    setSaving(true);
    try {
      const updated = await api.updateSiteSettings({ intro_splash_enabled: next });
      setSettings(updated);
      toast({
        title: next ? 'Intro splash resumed' : 'Intro splash paused',
        description: next
          ? 'Visitors will see the "SPRITELOCKER but BETTER" flash again on load.'
          : 'The flash will no longer appear for anyone (including you).',
      });
    } catch (err) {
      toast({ title: 'Save failed', description: String(err.message || err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const toggleMerchComingSoon = async () => {
    if (!settings || saving) return;
    const next = !settings.merch_coming_soon;
    setSaving(true);
    try {
      const updated = await api.updateSiteSettings({ merch_coming_soon: next });
      setSettings(updated);
      toast({
        title: next ? 'Coming Soon banner ON' : 'Coming Soon banner OFF',
        description: next
          ? 'A diagonal ribbon will flash across /merch when visitors open it.'
          : 'The merch page will open cleanly with no banner.',
      });
    } catch (err) {
      toast({ title: 'Save failed', description: String(err.message || err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="rounded-2xl border-2 border-[#3a1064] bg-white p-6 shadow-[0_4px_0_#3a1064] flex items-center gap-3 text-[#3a1064] font-black">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading site settings...
      </div>
    );
  }

  const on = !!settings.intro_splash_enabled;

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border-2 border-[#3a1064] bg-white p-6 shadow-[0_4px_0_#3a1064]">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-[#3a1064] flex items-center justify-center shrink-0">
            <Sparkles className="h-6 w-6 text-[#ffd900]" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[2px] text-[#3a1064]/70">
              Intro splash
            </div>
            <div className="text-lg sm:text-xl font-black text-[#3a1064] leading-tight">
              &ldquo;SPRITELOCKER but BETTER&rdquo; flash
            </div>
            <div className="text-sm font-bold text-[#3a1064]/80 mt-1">
              A 5-second full-screen intro that plays on every fresh page load. Pause it during events, screenshots, or if you just want to rest the joke for a while.
            </div>
            <div className="mt-3 inline-flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-[2px] border-2 ${
                  on
                    ? 'bg-[#2ee6c1] text-[#064733] border-[#064733]'
                    : 'bg-[#ff2bd6] text-white border-[#3a1064]'
                }`}
                data-testid="intro-splash-status"
              >
                {on ? (
                  <>
                    <Eye className="h-3 w-3" strokeWidth={3} /> Playing
                  </>
                ) : (
                  <>
                    <EyeOff className="h-3 w-3" strokeWidth={3} /> Paused
                  </>
                )}
              </span>
            </div>
          </div>
          <Button
            onClick={toggleIntroSplash}
            disabled={saving}
            data-testid="toggle-intro-splash"
            className={`h-11 px-5 font-black uppercase tracking-wider border-2 border-[#3a1064] shadow-[0_4px_0_#3a1064] ${
              on
                ? 'bg-[#ff2bd6] text-white hover:bg-[#e119bf]'
                : 'bg-[#ffd900] text-[#3a1064] hover:brightness-95'
            }`}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : on ? (
              <EyeOff className="h-4 w-4 mr-2" strokeWidth={3} />
            ) : (
              <Eye className="h-4 w-4 mr-2" strokeWidth={3} />
            )}
            {on ? 'Pause splash' : 'Resume splash'}
          </Button>
        </div>
      </div>

      {/* Merch coming-soon toggle */}
      <div className="rounded-2xl border-2 border-[#3a1064] bg-white p-6 shadow-[0_4px_0_#3a1064]">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-[#ff2bd6] flex items-center justify-center shrink-0">
            <ShoppingBag className="h-6 w-6 text-white" strokeWidth={3} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black uppercase tracking-[2px] text-[#3a1064]/70">
              Merch shop
            </div>
            <div className="text-lg sm:text-xl font-black text-[#3a1064] leading-tight">
              &ldquo;Coming Soon&rdquo; flash banner on /merch
            </div>
            <div className="text-sm font-bold text-[#3a1064]/80 mt-1">
              Slides a diagonal COMING SOON ribbon across the screen for a
              few seconds when the merch page opens. The shop stays visible
              and interactive underneath — great for teasing an upcoming drop
              without hiding the products.
            </div>
            <div className="mt-3 inline-flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-[2px] border-2 ${
                  settings.merch_coming_soon
                    ? 'bg-[#ff2bd6] text-white border-[#3a1064]'
                    : 'bg-[#2ee6c1] text-[#064733] border-[#064733]'
                }`}
                data-testid="merch-coming-soon-status"
              >
                {settings.merch_coming_soon ? (
                  <><EyeOff className="h-3 w-3" strokeWidth={3} /> Banner ON</>
                ) : (
                  <><Eye className="h-3 w-3" strokeWidth={3} /> Banner OFF</>
                )}
              </span>
            </div>
          </div>
          <Button
            onClick={toggleMerchComingSoon}
            disabled={saving}
            data-testid="toggle-merch-coming-soon"
            className={`h-11 px-5 font-black uppercase tracking-wider border-2 border-[#3a1064] shadow-[0_4px_0_#3a1064] ${
              settings.merch_coming_soon
                ? 'bg-[#ffd900] text-[#3a1064] hover:brightness-95'
                : 'bg-[#ff2bd6] text-white hover:bg-[#e119bf]'
            }`}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : settings.merch_coming_soon ? (
              <Eye className="h-4 w-4 mr-2" strokeWidth={3} />
            ) : (
              <EyeOff className="h-4 w-4 mr-2" strokeWidth={3} />
            )}
            {settings.merch_coming_soon ? 'Turn banner off' : 'Turn banner on'}
          </Button>
        </div>
      </div>

      <FortniteSyncAdminCard />
      <SeasonManagerCard />
    </div>
  );
}


// ─── Fortnite.gg auto-sync ───────────────────────────────────────────────────
function FortniteSyncAdminCard() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [pastedHtml, setPastedHtml] = useState('');

  const refresh = useCallback(async () => {
    try {
      const s = await api.getFortniteSync();
      setStatus(s);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async () => {
    if (!status) return;
    try {
      const s = await api.toggleFortniteSync(!status.enabled);
      setStatus(s);
      toast({ title: s.enabled ? 'Auto-sync ON' : 'Auto-sync OFF' });
    } catch (e) {
      toast({ title: 'Save failed', description: String(e.message || e), variant: 'destructive' });
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const s = await api.runFortniteSync();
      setStatus(s);
      toast({
        title: `Sync finished`,
        description: s.last_sync_error
          ? `Blocked by fortnite.gg: use "Paste HTML" below.`
          : `${s.last_sync_added} new sprites found. Total tracked: ${s.total_dynamic}`,
        variant: s.last_sync_error ? 'destructive' : 'default',
      });
    } catch (e) {
      toast({ title: 'Sync failed', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  const importPaste = async () => {
    if (!pastedHtml.trim()) return;
    setRunning(true);
    try {
      const s = await api.importFortniteHtml(pastedHtml);
      setStatus(s);
      setPastedHtml('');
      toast({
        title: `Imported ${s.last_sync_added} new sprites`,
        description: `Total tracked: ${s.total_dynamic}`,
      });
    } catch (e) {
      toast({ title: 'Import failed', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setRunning(false);
    }
  };

  if (!status) {
    return (
      <div className="rounded-2xl border-2 border-[#3a1064] bg-white p-6 shadow-[0_4px_0_#3a1064] flex items-center gap-3 text-[#3a1064] font-black">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading fortnite.gg sync...
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-[#3a1064] bg-white p-6 shadow-[0_4px_0_#3a1064]">
      <div className="flex items-start gap-4 mb-4">
        <div className="h-12 w-12 rounded-xl bg-[#ff2bd6] flex items-center justify-center shrink-0">
          <Radio className="h-6 w-6 text-white" strokeWidth={3} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-black uppercase tracking-[2px] text-[#3a1064]/70">
            Fortnite.gg auto-sync
          </div>
          <div className="text-lg sm:text-xl font-black text-[#3a1064] leading-tight">
            Daily sprite roster sync
          </div>
          <div className="text-sm font-bold text-[#3a1064]/80 mt-1">
            When enabled, the site checks fortnite.gg every 24h and adds any newly-listed sprites to the vault automatically. Discovered:{' '}
            <span className="text-[#ff2bd6]">{status.total_dynamic}</span> so far.
            {status.last_sync_at && (
              <span> · Last check: {new Date(status.last_sync_at).toLocaleString()}</span>
            )}
          </div>
        </div>
        <Button
          onClick={toggle}
          data-testid="toggle-fortnite-sync"
          className={`h-11 px-5 font-black uppercase tracking-wider border-2 border-[#3a1064] shadow-[0_4px_0_#3a1064] ${
            status.enabled
              ? 'bg-[#2ee6c1] text-[#064733] hover:brightness-95'
              : 'bg-[#ffd900] text-[#3a1064] hover:brightness-95'
          }`}
        >
          {status.enabled ? 'ON' : 'OFF'}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Button onClick={runNow} disabled={running} data-testid="run-fortnite-sync"
          className="h-9 px-4 bg-[#ff2bd6] text-white font-black border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064]">
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" strokeWidth={3} />}
          Sync now
        </Button>
      </div>

      {(status.last_sync_new_families?.length > 0 || status.last_rollover) && (
        <div className="mt-3 p-3 rounded-lg bg-[#e6fff8] border-2 border-[#3a1064] text-[#3a1064] text-xs font-bold" data-testid="sync-season-info">
          {status.last_rollover && <div>Season rolled over to <span className="font-black">{status.last_rollover}</span></div>}
          {status.last_sync_new_families?.length > 0 && (
            <div>New families: <span className="font-black">{status.last_sync_new_families.join(', ')}</span></div>
          )}
        </div>
      )}

      {status.last_sync_error && (
        <div className="mt-3 p-3 rounded-lg bg-[#fff7d6] border-2 border-[#3a1064] text-[#3a1064] text-xs font-bold">
          Last error: <span className="font-black">{status.last_sync_error}</span>
          <div className="mt-1 opacity-80">
            Fortnite.gg blocks server-side crawlers. Use the paste-HTML fallback below — open{' '}
            <a href="https://fortnite.gg/sprites?show=all" target="_blank" rel="noreferrer" className="underline">fortnite.gg/sprites</a>,
            view page source, copy the entire HTML, paste it in, then click Import.
          </div>
        </div>
      )}

      <div className="mt-3">
        <div className="text-[10px] font-black uppercase tracking-[2px] text-[#3a1064]/70 mb-1">
          Paste HTML fallback
        </div>
        <textarea
          value={pastedHtml}
          onChange={(e) => setPastedHtml(e.target.value)}
          placeholder="Paste the full HTML from fortnite.gg/sprites?show=all here..."
          rows={4}
          data-testid="paste-fortnite-html"
          className="w-full rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-xs font-mono text-[#3a1064] bg-[#fff7e0] placeholder:text-[#3a1064]/40"
        />
        <Button onClick={importPaste} disabled={running || !pastedHtml.trim()}
          className="mt-2 h-9 px-4 bg-[#3a1064] text-[#ffd900] font-black border-2 border-[#3a1064] shadow-[0_3px_0_#1a0440]">
          {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Import pasted HTML
        </Button>
      </div>
    </div>
  );
}


// ─── Blog Admin Card ───────────────────────────────────────────────────────
// Embeds the Soro-hosted blog dashboard inside the Admin Panel. Soro ships a
// single defer'd script that hydrates the `#soro-blog` container, so we only
// need to inject the script once (and guard against re-injects on tab switch).
const SORO_SRC = 'https://app.trysoro.com/api/embed/80f2516c-24b6-47fc-ad37-faf08904447e';

function BlogAdminCard({ active }) {
  useEffect(() => {
    if (!active) return;
    // Skip if script already present on the page
    if (document.querySelector(`script[src="${SORO_SRC}"]`)) return;
    const s = document.createElement('script');
    s.src = SORO_SRC;
    s.defer = true;
    document.body.appendChild(s);
  }, [active]);

  return (
    <div
      data-testid="admin-blog-card"
      className="bg-white rounded-2xl border-[3px] border-[#3a1064] shadow-[0_6px_0_#3a1064] p-4 sm:p-6"
    >
      <div className="flex items-center gap-2 mb-4">
        <Newspaper className="h-5 w-5 text-[#3a1064]" />
        <h2 className="font-black text-xl text-[#3a1064] tracking-wide">Blog Manager</h2>
      </div>
      <p className="text-sm text-[#3a1064]/70 mb-4">
        Author and publish posts through the embedded Soro dashboard below. Changes
        propagate to your public blog automatically.
      </p>
      {/* Soro injects its UI into this container */}
      <div
        id="soro-blog"
        data-testid="soro-blog-container"
        className="min-h-[600px] rounded-xl border border-[#3a1064]/20 bg-[#fff7e0]/40"
      />
    </div>
  );
}


// ─── Trades Admin Panel ───────────────────────────────────────────────────
// Lists every trade in the system with filters (status + user search) and
// surfaces "stale traders" — users sitting on pending requests > 24h.
// Backed by /api/admin/trades and /api/admin/trades/stale.
function TradesAdminPanel({ active }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({ total: 0, pending: 0, accepted: 0, declined: 0, cancelled: 0 });
  const [trades, setTrades] = useState([]);
  const [stale, setStale] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [userQuery, setUserQuery] = useState('');
  const [staleHours, setStaleHours] = useState(24);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [tradesRes, staleRes] = await Promise.all([
        api.adminListTrades({
          status: statusFilter || undefined,
          user: userQuery.trim() || undefined,
          limit: 200,
        }),
        api.adminStaleTraders(staleHours).catch(() => []),
      ]);
      setStats(tradesRes.stats || { total: 0, pending: 0, accepted: 0, declined: 0, cancelled: 0 });
      setTrades(tradesRes.trades || []);
      setStale(staleRes || []);
    } catch (e) {
      setError(e.message || 'Failed to load trades');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!active) return;
    load();
  }, [active]);

  const badgeFor = (status) => {
    const colors = {
      pending: '#ff9a1c',
      accepted: '#2ee6c1',
      declined: '#ff2f6d',
      cancelled: '#94a3b8',
    };
    return (
      <span
        className="inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider"
        style={{ backgroundColor: colors[status] || '#94a3b8', color: '#0a0018' }}
      >
        {status}
      </span>
    );
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-trades-card">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <SummaryCard label="Total"     value={stats.total}     accent="#3a1064" fg="#ffd900" />
        <SummaryCard label="Pending"   value={stats.pending}   accent="#ff9a1c" fg="#0a0018" />
        <SummaryCard label="Accepted"  value={stats.accepted}  accent="#2ee6c1" fg="#0a0018" />
        <SummaryCard label="Declined"  value={stats.declined}  accent="#ff2f6d" fg="#fff" />
        <SummaryCard label="Cancelled" value={stats.cancelled} accent="#94a3b8" fg="#0a0018" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border-[3px] border-[#3a1064] shadow-[0_6px_0_#3a1064] p-4 sm:p-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">
              Search by username / user id
            </label>
            <input
              type="text"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="username or user id"
              data-testid="admin-trades-user-filter"
              className="w-full rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-sm font-bold text-[#3a1064] bg-[#fff7e0] placeholder:text-[#3a1064]/40"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="admin-trades-status-filter"
              className="rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-sm font-black text-[#3a1064] bg-[#fff7e0]"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="declined">Declined</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <Button
            onClick={load}
            disabled={loading}
            data-testid="admin-trades-refresh"
            className="h-10 px-5 bg-[#3a1064] text-[#ffd900] font-black border-2 border-[#3a1064] shadow-[0_3px_0_#1a0440]"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Apply
          </Button>
        </div>
        {error && (
          <div className="mt-3 rounded-xl bg-[#ff2f6d]/15 border-2 border-[#ff2f6d] px-3 py-2 text-sm font-black text-[#3a1064]">
            {error}
          </div>
        )}
      </div>

      {/* Trades list */}
      <div className="bg-white rounded-2xl border-[3px] border-[#3a1064] shadow-[0_6px_0_#3a1064] p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black text-lg text-[#3a1064] tracking-wide">
            All trades {loading ? '(loading…)' : `(${trades.length})`}
          </h3>
        </div>
        {trades.length === 0 && !loading ? (
          <div className="py-8 text-center text-[#3a1064]/60 font-bold">
            No trades match the current filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 border-b-2 border-[#3a1064]/20">
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">From</th>
                  <th className="py-2 pr-3">→</th>
                  <th className="py-2 pr-3">To</th>
                  <th className="py-2 pr-3">Sprites</th>
                  <th className="py-2 pr-3">Created</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t) => (
                  <tr key={t.id} className="border-b border-[#3a1064]/10 hover:bg-[#fff7e0]/60">
                    <td className="py-2 pr-3">{badgeFor(t.status)}</td>
                    <td className="py-2 pr-3 font-black text-[#3a1064]">
                      {t.sender_username || t.sender_name || t.sender_id?.slice(-6) || '?'}
                    </td>
                    <td className="py-2 pr-3 text-[#3a1064]/50">→</td>
                    <td className="py-2 pr-3 font-black text-[#3a1064]">
                      {t.recipient_username || t.recipient_name || t.recipient_id?.slice(-6) || '?'}
                    </td>
                    <td className="py-2 pr-3 text-[#3a1064]/70">
                      {t.offer_sprite_id ? `${t.offer_sprite_id} → ${t.request_sprite_id || '?'}` : '—'}
                    </td>
                    <td className="py-2 pr-3 text-[#3a1064]/70 whitespace-nowrap">
                      {fmtDate(t.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stale traders */}
      <div className="bg-white rounded-2xl border-[3px] border-[#ff2f6d] shadow-[0_6px_0_#ff2f6d] p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black text-lg text-[#3a1064] tracking-wide">
            Stale traders (pending &gt;
            <input
              type="number"
              value={staleHours}
              min={1}
              onChange={(e) => setStaleHours(Number(e.target.value) || 24)}
              onBlur={load}
              className="mx-1 w-14 text-center rounded-md border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-1 py-0.5 text-sm font-black text-[#3a1064] bg-[#fff7e0]"
              data-testid="admin-trades-stale-hours"
            />
            h)
          </h3>
        </div>
        {stale.length === 0 ? (
          <div className="py-4 text-center text-[#3a1064]/60 font-bold">
            🎉 No one is sitting on stale trades right now.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 border-b-2 border-[#3a1064]/20">
                  <th className="py-2 pr-3">User</th>
                  <th className="py-2 pr-3">Pending</th>
                  <th className="py-2 pr-3">Waiting</th>
                  <th className="py-2 pr-3">Oldest</th>
                </tr>
              </thead>
              <tbody>
                {stale.map((s) => (
                  <tr key={s.user_id} className="border-b border-[#3a1064]/10 hover:bg-[#fff7e0]/60">
                    <td className="py-2 pr-3 font-black text-[#3a1064]">
                      {s.username || s.name || s.email || s.user_id?.slice(-6)}
                    </td>
                    <td className="py-2 pr-3 text-[#3a1064]">{s.pending_received}</td>
                    <td className="py-2 pr-3 text-[#ff2f6d] font-black">
                      {s.hours_waiting ? `${Math.round(s.hours_waiting)}h` : '—'}
                    </td>
                    <td className="py-2 pr-3 text-[#3a1064]/70 whitespace-nowrap">
                      {fmtDate(s.oldest_pending_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Merch Admin Card ─────────────────────────────────────────────────────
// Full CRUD for the Sprite Vault store: list current products, edit inline,
// delete, seed the 3D-print starter set, and — key feature — paste any
// Amazon / AliExpress / Etsy / Shopify URL to auto-extract title, image,
// description and price into an editable draft before saving. Backed by
// /api/merch/admin/* endpoints.
const MERCH_CATS = [
  '3d-print', 'plush', 'apparel', 'poster', 'accessory', 'collectible', 'digital', 'other',
];
const INV_TYPES = [
  { value: 'affiliate', label: 'Affiliate link (external)' },
  { value: 'stripe',    label: 'In-house / Stripe checkout' },
];

const EMPTY_DRAFT = {
  title: '', description: '', image: '', category: 'other', tags: [],
  price_cents: '', currency: 'USD', external_url: '',
  inventory_type: 'affiliate', stock: '', featured: false, in_stock: true,
  source: 'manual', source_url: '',
};

function MerchAdminCard({ active }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState(null);
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const list = await api.listMerch({ limit: 200 });
      setProducts(list);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (active) load(); }, [active]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const resetDraft = () => { setDraft(EMPTY_DRAFT); setEditingId(null); };

  const startEdit = (p) => {
    setEditingId(p.id);
    setDraft({
      ...EMPTY_DRAFT,
      ...p,
      // price_cents is stored as int; keep it as string in the input to preserve empty state
      price_cents: p.price_cents == null ? '' : String(p.price_cents),
      stock: p.stock == null ? '' : String(p.stock),
      tags: p.tags || [],
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const runImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    setError('');
    try {
      const d = await api.adminImportMerchUrl(importUrl.trim(), draft.category || 'other');
      setDraft((cur) => ({
        ...cur,
        title: d.title || cur.title,
        description: d.description || cur.description,
        image: d.image || cur.image,
        external_url: d.external_url || cur.external_url,
        source_url: d.external_url || cur.source_url,
        source: d.source || 'custom',
        price_cents: d.price_cents == null ? cur.price_cents : String(d.price_cents),
      }));
    } catch (e) {
      setError('Could not import: ' + (e.message || 'unknown error'));
    } finally {
      setImporting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...draft,
        price_cents: draft.price_cents === '' ? null : parseInt(draft.price_cents, 10),
        stock: draft.stock === '' ? null : parseInt(draft.stock, 10),
        tags: Array.isArray(draft.tags) ? draft.tags : String(draft.tags || '').split(',').map((t) => t.trim()).filter(Boolean),
      };
      if (editingId) {
        await api.adminUpdateMerch(editingId, payload);
      } else {
        await api.adminCreateMerch(payload);
      }
      resetDraft();
      await load();
    } catch (e) {
      setError('Save failed: ' + (e.message || 'unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await api.adminDeleteMerch(id);
      await load();
    } catch (e) {
      setError('Delete failed: ' + (e.message || 'unknown error'));
    }
  };

  const seed3d = async () => {
    if (!window.confirm('Seed 5 starter 3D-printed sprite listings? Existing ones with the same title will be skipped.')) return;
    try {
      await api.adminSeed3dPrints();
      await load();
    } catch (e) {
      setError('Seed failed: ' + (e.message || 'unknown error'));
    }
  };

  const priceInDollars = draft.price_cents === '' || draft.price_cents == null
    ? ''
    : (Number(draft.price_cents) / 100).toFixed(2);

  return (
    <div className="space-y-4" data-testid="admin-merch-card">
      {/* URL Importer */}
      <div className="bg-white rounded-2xl border-[3px] border-[#3a1064] shadow-[0_6px_0_#3a1064] p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-2">
          <ShoppingBag className="h-5 w-5 text-[#3a1064]" />
          <h2 className="font-black text-xl text-[#3a1064] tracking-wide">
            {editingId ? 'Edit product' : 'Add a product'}
          </h2>
        </div>
        <p className="text-sm text-[#3a1064]/70 mb-4">
          Paste an Amazon / AliExpress / Etsy / Shopify URL to auto-fill title, image, description, and price. Review, edit, then Save.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <input
            type="url"
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="https://www.amazon.com/dp/..."
            data-testid="merch-import-url"
            className="flex-1 rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-sm font-bold text-[#3a1064] bg-[#fff7e0] placeholder:text-[#3a1064]/40"
          />
          <Button
            onClick={runImport}
            disabled={importing || !importUrl.trim()}
            data-testid="merch-import-btn"
            className="h-10 px-5 bg-[#ff2bd6] text-white font-black border-2 border-[#0a0018] shadow-[0_3px_0_#0a0018]"
          >
            {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
            Auto-fill from URL
          </Button>
        </div>

        {/* Draft form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <LabelInput label="Title" value={draft.title} onChange={(v) => setField('title', v)} testid="merch-title" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">Description</label>
            <textarea
              rows={3}
              value={draft.description}
              onChange={(e) => setField('description', e.target.value)}
              className="w-full rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-sm font-bold text-[#3a1064] bg-[#fff7e0]"
              data-testid="merch-description"
            />
          </div>
          <LabelInput label="Image URL" value={draft.image} onChange={(v) => setField('image', v)} testid="merch-image" />
          <LabelInput label="External / affiliate URL" value={draft.external_url} onChange={(v) => setField('external_url', v)} testid="merch-external-url" />
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">Price (USD)</label>
            <input
              type="number"
              step="0.01"
              value={priceInDollars}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') setField('price_cents', '');
                else setField('price_cents', String(Math.round(parseFloat(v) * 100)));
              }}
              placeholder="e.g. 24.99"
              className="w-full rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-sm font-bold text-[#3a1064] bg-[#fff7e0]"
              data-testid="merch-price"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">Category</label>
            <select
              value={draft.category}
              onChange={(e) => setField('category', e.target.value)}
              className="w-full rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-sm font-black text-[#3a1064] bg-[#fff7e0]"
              data-testid="merch-category"
            >
              {MERCH_CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">Inventory type</label>
            <select
              value={draft.inventory_type}
              onChange={(e) => setField('inventory_type', e.target.value)}
              className="w-full rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-sm font-black text-[#3a1064] bg-[#fff7e0]"
            >
              {INV_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">Stock (optional)</label>
            <input
              type="number"
              value={draft.stock}
              onChange={(e) => setField('stock', e.target.value)}
              className="w-full rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-sm font-bold text-[#3a1064] bg-[#fff7e0]"
            />
          </div>
          <div className="flex items-center gap-4 pt-4">
            <label className="inline-flex items-center gap-2 font-black text-sm text-[#3a1064]">
              <input
                type="checkbox"
                checked={!!draft.featured}
                onChange={(e) => setField('featured', e.target.checked)}
                className="h-4 w-4"
              />
              Featured
            </label>
            <label className="inline-flex items-center gap-2 font-black text-sm text-[#3a1064]">
              <input
                type="checkbox"
                checked={!!draft.in_stock}
                onChange={(e) => setField('in_stock', e.target.checked)}
                className="h-4 w-4"
              />
              In stock
            </label>
          </div>
        </div>

        {error && (
          <div className="mt-3 rounded-xl bg-[#ff2f6d]/15 border-2 border-[#ff2f6d] px-3 py-2 text-sm font-black text-[#3a1064]">
            {error}
          </div>
        )}

        <div className="mt-4 flex gap-2 flex-wrap">
          <Button
            onClick={save}
            disabled={saving || !draft.title}
            data-testid="merch-save-btn"
            className="h-10 px-5 bg-[#3a1064] text-[#ffd900] font-black border-2 border-[#3a1064] shadow-[0_3px_0_#1a0440]"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {editingId ? 'Update product' : 'Create product'}
          </Button>
          {editingId && (
            <Button
              onClick={resetDraft}
              className="h-10 px-4 bg-white text-[#3a1064] font-black border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064]"
            >
              Cancel edit
            </Button>
          )}
          <Button
            onClick={seed3d}
            className="h-10 px-4 ml-auto bg-[#2ee6c1] text-[#0a0018] font-black border-2 border-[#0a0018] shadow-[0_3px_0_#0a0018]"
            data-testid="merch-seed-3d-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Seed 3D prints
          </Button>
        </div>
      </div>

      {/* Product list */}
      <div className="bg-white rounded-2xl border-[3px] border-[#3a1064] shadow-[0_6px_0_#3a1064] p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-black text-lg text-[#3a1064] tracking-wide">
            Products {loading ? '(loading…)' : `(${products.length})`}
          </h3>
          <Button
            onClick={load}
            className="h-8 px-3 bg-white text-[#3a1064] font-black border-2 border-[#3a1064] shadow-[0_2px_0_#3a1064] text-xs"
          >
            <RefreshCw className="h-3 w-3 mr-1" /> Refresh
          </Button>
        </div>
        {products.length === 0 && !loading ? (
          <div className="py-8 text-center text-[#3a1064]/60 font-bold">
            No products yet — add one above or click &quot;Seed 3D prints&quot;.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {products.map((p) => (
              <div key={p.id} className="rounded-xl border-2 border-[#3a1064]/25 bg-[#fff7e0]/40 p-3 flex gap-3">
                <div className="w-16 h-16 rounded-lg bg-[#3a1064]/10 flex-shrink-0 overflow-hidden flex items-center justify-center">
                  {p.image ? (
                    <img src={p.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  ) : (
                    <ShoppingBag className="h-6 w-6 text-[#3a1064]/40" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-sm text-[#3a1064] truncate">{p.title}</div>
                  <div className="text-[10px] font-black uppercase tracking-wider text-[#3a1064]/60">
                    {p.category} · {p.inventory_type}
                    {p.featured && ' · ⭐ FEATURED'}
                    {!p.in_stock && ' · SOLD OUT'}
                  </div>
                  <div className="text-xs font-black text-[#3a1064] mt-1">
                    {p.price_cents != null ? `$${(p.price_cents / 100).toFixed(2)}` : 'No price'}
                  </div>
                  <div className="mt-1.5 flex gap-1.5">
                    <button
                      onClick={() => startEdit(p)}
                      className="text-[10px] font-black uppercase tracking-wider text-[#3a1064] bg-white border-2 border-[#3a1064] rounded-md px-2 py-0.5"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => removeProduct(p.id)}
                      className="text-[10px] font-black uppercase tracking-wider text-white bg-[#ff2f6d] border-2 border-[#0a0018] rounded-md px-2 py-0.5"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LabelInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] font-black uppercase tracking-wider text-[#3a1064]/70 mb-1">{label}</label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-xl border-2 border-[#3a1064]/40 focus:border-[#3a1064] focus:outline-none px-3 py-2 text-sm font-bold text-[#3a1064] bg-[#fff7e0]"
      />
    </div>
  );
}

